import React, { useContext } from 'react';

import { Link } from 'react-router-dom';
import { Table, Dropdown, Button } from 'semantic';
import { useTranslation } from 'react-i18next';

import InvoiceTypeLabel from 'components/InvoiceTypeLabel';
import { Search } from 'components';
import Currency from 'components/Currency';
import InvoiceStatusLabel from 'components/InvoiceStatusLabel';
import ResourceLink from 'components/Link/ResourceLink';
import { Invoice, INVOICE_TYPE } from 'types/invoice';
import Actions from '../Actions';
import NetSuiteSyncStatus from 'components/NetSuiteSyncStatus';
import { FE_INVOICES_PATH } from '../utils';
import { getFilePath } from 'utils/api';

export default function InvoiceTable() {
  const { items, loading, reload, setSort, getSorted } = useContext<any>(
    Search.Context
  );
  const { t } = useTranslation();
  const invoices = items as Invoice[];

  if (!items.length || loading) return null;

  return (
    <>
      <Table celled sortable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {t('invoices.columnNumber', 'Number')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('invoices.columnProvider', 'Provider')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('invoices.columnAccount', 'Account')}
            </Table.HeaderCell>
            <Table.HeaderCell width={2}>
              {t('invoices.columnDate', 'Date')}
            </Table.HeaderCell>
            <Table.HeaderCell width={1}>
              {t('invoices.columnType', 'Type')}
            </Table.HeaderCell>
            <Table.HeaderCell
              width={2}
              sorted={getSorted('totalAmount')}
              onClick={() => setSort('totalAmount')}>
              {t('invoices.columnPrice', 'Price')}
            </Table.HeaderCell>
            <Table.HeaderCell width={1}>
              {t('invoices.columnStatus', 'Status')}
            </Table.HeaderCell>
            <Table.HeaderCell width={1}>
              {t('invoices.columnERP', 'ERP')}
            </Table.HeaderCell>
            <Table.HeaderCell width={3}>
              {t('invoices.columnActions', 'Actions')}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {invoices.map((item: Invoice) => {
            const totalAmount =
              item.type === INVOICE_TYPE.usageReimbursement ||
              item.type === INVOICE_TYPE.usageReimbursementDelayedSessions
                ? item.totalCreditAmountWithVat
                : item.totalAmountWithVat;

            return (
              <Table.Row key={item.id}>
                <Table.Cell>
                  <ResourceLink
                    target="_blank"
                    route={`${FE_INVOICES_PATH}/${item.id}?providerId=${item.providerId}`}
                    searchItemProviderId={item.providerId}
                    linkName={item.identifier || ''}
                  />
                </Table.Cell>
                <Table.Cell>{item.provider?.name}</Table.Cell>
                <Table.Cell>
                  <ResourceLink
                    target="_blank"
                    route={`/accounts/${item.accountId}?providerId=${item.providerId}`}
                    searchItemProviderId={item.providerId}
                    linkName={item.account?.name || ''}
                  />
                  {item.user && (
                    <p>
                      <small>
                        (
                        <ResourceLink
                          target="_blank"
                          route={`/accounts/users/${item?.user?.id}?providerId=${item.providerId}`}
                          searchItemProviderId={item.providerId}
                          linkName={item?.user?.name || ''}
                        />
                        )
                      </small>
                    </p>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {item.year} / {item.month}
                </Table.Cell>
                <Table.Cell>
                  <InvoiceTypeLabel invoice={item} />
                </Table.Cell>
                <Table.Cell>
                  <Currency currency={item.currency} value={totalAmount} />
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <InvoiceStatusLabel invoice={item} />
                </Table.Cell>
                <Table.Cell textAlign="center">
                  <NetSuiteSyncStatus
                    invoiceId={item?.id}
                    status={item?.netSuiteStatus as any}
                    disabledSync
                  />
                </Table.Cell>
                <Table.Cell textAlign="center">
                  <Button
                    basic
                    icon="download"
                    title="Download"
                    href={getFilePath(
                      `/1/invoices/${item.id}/pdf?accessToken=${item.accessToken}`
                    )}
                    as="a"
                    disabled={!item.isReady}
                  />
                  <Dropdown button basic text={t('common.more', 'More')}>
                    <Dropdown.Menu direction="left">
                      <Actions item={item} onReload={reload} />
                    </Dropdown.Menu>
                  </Dropdown>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
}
